import { useQuery } from '@tanstack/react-query'
import { ReactNode } from 'react'

/**
 * Which Team Tools surfaces are switched on.
 *
 * A kill switch that only made the endpoints refuse would leave its tab sitting
 * in the navigation, opening onto a page of errors. A control that is present
 * and broken reads as a bug and sends somebody debugging; a control that is
 * absent reads as a decision. So the nav asks the server what exists before it
 * draws itself.
 *
 * This is a hint for drawing a menu and nothing more. Every Skins endpoint
 * checks the switch for itself, so a stale bundle that never asked, or somebody
 * typing the URL, is still refused by the server.
 *
 * Served from Team Tools rather than added to the viewer endpoint on purpose.
 * That one is fetched by every player on every page load, and login is already
 * the largest single consumer of database CPU here; a read added there to tell a
 * handful of staff whether one tab is on would be charged to the whole player
 * base.
 */
export type Features = {
    /** Undefined until the answer arrives, and from a server too old to say. */
    skins?: boolean
    /** Everything this viewer may do, by role flag or by a grant. See Capabilities.tsx. */
    capabilities?: string[]
    /** Only what was granted in the admin, ignoring role flags. See Capabilities.tsx. */
    granted?: string[]
    /** Whether any of the four role flags is set. */
    is_role_staff?: boolean
}

export const useFeatures = () =>
    useQuery<Features>({
        queryKey: ['team-tools', 'features'],
        queryFn: async () => {
            const response = await fetch('/team-tools/api/features')
            if (!response.ok) throw new Error('features unavailable')
            return response.json()
        },
        // These change when somebody throws a switch by hand, which is rare and
        // never mid-click. One fetch per session is the right amount of asking.
        staleTime: Infinity,
        retry: false,
    })

/**
 * Whether one surface is on, defaulting to ON while the answer is unknown.
 *
 * Unknown covers three cases that must not be told apart here: the request is
 * still in flight, it failed, and the server is older than the flag. Defaulting
 * to on means the worst case is a tab that appears for a moment and then goes,
 * against a tab that vanishes for everybody the first time this endpoint has a
 * bad minute. Nothing is exposed by guessing generously, because the server
 * refuses either way.
 */
export const useFeature = (name: keyof Features): boolean => {
    const { data } = useFeatures()
    return data?.[name] !== false
}

/**
 * Renders its children only while `name` is switched on.
 *
 * `fallback` is for a route, which needs to say something: somebody who followed
 * a bookmark to a switched-off page should be told it is off, not shown an empty
 * screen they will read as a broken build. The nav passes no fallback, because a
 * menu entry that is off should simply not be there.
 */
export const FeatureEnabled = ({
    name,
    children,
    fallback = null,
}: {
    name: keyof Features
    children: ReactNode
    fallback?: ReactNode
}) => (useFeature(name) ? <>{children}</> : <>{fallback}</>)

/** The Skins nav entry, present only while Skins is switched on. */
export const SkinsEnabled = ({ children }: { children: ReactNode }) => (
    <FeatureEnabled name='skins'>{children}</FeatureEnabled>
)

/** What a bookmark to a switched-off Team Tools page shows. */
export const FeatureOff = ({ title }: { title: string }) => (
    <div className='mx-auto mt-16 max-w-md border border-white border-opacity-20 p-6 text-center'>
        <p className='text-sm font-bold uppercase tracking-wide text-white'>{title} is switched off</p>
        <p className='mt-2 text-xs text-white text-opacity-50'>
            Somebody turned this off deliberately. It can be switched back on from the Django admin,
            under {title} Kill Switch.
        </p>
    </div>
)
